import { Component } from '@theme/component';

/**
 * A custom element wrapping the floating product configurator card
 * (blocks/_product-configurator.liquid). On desktop the card is pulled out
 * of grid flow via `position: absolute` so it can overlay the product media,
 * which means the grid never reserves space for it and `.product-details`
 * below can end up overlapped once the configurator grows tall enough that
 * its bottom edge drops past the media's bottom edge (e.g. adding Kissen
 * rows). This tracks that overhang — configurator bottom minus media bottom,
 * floored at 0 so a configurator that's shorter than the media never pushes
 * anything down — and publishes it as `--product-configurator-overhang`,
 * which `.product-details` then uses as its own `margin-block-start`.
 *
 * The property is set on `.product-information__grid` rather than on this
 * element itself: `.product-details` is a sibling, not a descendant, of
 * `<product-configurator>`, and custom properties only inherit down the DOM
 * tree, so it has to be written on a shared ancestor both elements sit
 * inside (see snippets/product-information-content.liquid).
 *
 * Measured via getBoundingClientRect rather than by combining inset-block-
 * start with the observed height: the two elements' vertical offset from
 * each other can come from several places (insets, margins, the media's own
 * intrinsic aspect ratio), and comparing rendered bottom edges directly stays
 * correct regardless of how that offset happens to be expressed in CSS.
 *
 * @extends {Component}
 */
class ProductConfiguratorComponent extends Component {
  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(() => this.#updateOverhang());

  #updateOverhang() {
    const grid = this.closest('.product-information__grid');
    const media = document.querySelector('.product-information__media');
    if (!grid) return;

    const overhang = media ? Math.max(0, this.getBoundingClientRect().bottom - media.getBoundingClientRect().bottom) : 0;
    grid.style.setProperty('--product-configurator-overhang', `${Math.round(overhang)}px`);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    const media = document.querySelector('.product-information__media');
    if (media) this.#resizeObserver.observe(media);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfiguratorComponent);
}
